import React from "react";
import { CRMSpacing } from "../../../../models/CRMSpacing";
import { PopupBase } from "../../../Simple/PopupBase/PopupBase";
import { Sticky } from "../../../BuildingBlocks/Sticky";
import { CRMTidyPopout } from "../../../Simple/CRMTidyPopout/CRMTidyPopout";
import { Padding } from "../../../BuildingBlocks/Padding";
import { SpacingColumn } from "../../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../../BuildingBlocks/SpacingRow";
import { CRMButtonSecondary } from "../../../CRMButtonSecondary/CRMButtonSecondary";
import { CRMButtonTertiary } from "../../../CRMButtonTertiary/CRMButtonTertiary";

export type TQualityScoreSuggestion = {
    dimension: "clarity" | "tone" | "ownership" | "length";
    issue: string;
    suggestion: string;
};

export type TQualityScoreData = {
    score_status: "scored" | "skipped_too_short" | "skipped_template" | "ai_unavailable";
    clarity_score: number | null;
    tone_score: number | null;
    ownership_score: number | null;
    verdict: "send_confidently" | "worth_a_tweak" | "reconsider" | null;
    suggestions: ReadonlyArray<TQualityScoreSuggestion>;
};

type TCRMEmailQualityScoreModalProps = {
    score: TQualityScoreData;
    onSendAnyway: () => void;
    onLetMeEdit: () => void;
    onRunAiRewrite: () => void;
    rewriteStatus: "idle" | "loading" | "success" | "error";
};

type TRag = "red" | "amber" | "green" | "none";

const ragForScore = (score: number | null): TRag => {
    if (score === null) return "none";
    if (score <= 3) return "red";
    if (score < 7) return "amber";
    return "green";
};

const VERDICT_LABEL: Record<"send_confidently" | "worth_a_tweak" | "reconsider", string> = {
    send_confidently: "Send confidently",
    worth_a_tweak: "Worth a tweak",
    reconsider: "Reconsider",
};

const DIMENSION_LABELS: Record<"clarity" | "tone" | "ownership" | "length", string> = {
    clarity: "Clarity & next steps",
    tone: "Tone",
    ownership: "Ownership",
    length: "Length",
};

const DimensionPill = (props: { label: string; score: number | null }): JSX.Element => {
    const rag = ragForScore(props.score);
    return (
        <div className="crm-email-quality-score-modal__pill" aria-label={`${props.label}: ${rag === "none" ? "not scored" : rag}`}>
            <span className={`crm-email-quality-score-modal__dot crm-email-quality-score-modal__dot--${rag}`} aria-hidden="true" />
            <span className="crm-email-quality-score-modal__pill-label">{props.label}</span>
            {props.score !== null && <span className="crm-email-quality-score-modal__pill-score">{props.score}/10</span>}
        </div>
    );
};

export const CRMEmailQualityScoreModal = (props: TCRMEmailQualityScoreModalProps): JSX.Element => {
    const verdictKey = props.score.verdict ?? "worth_a_tweak";
    return (
        <PopupBase padding={CRMSpacing.MEDIUM} isOpen={true}>
            <Sticky top="80px">
                <CRMTidyPopout width="100%" maxHeight="85vh" overflowY="auto">
                    <Padding spacing={CRMSpacing.MEDIUM}>
                        <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                            <div className={`crm-email-quality-score-modal__verdict crm-email-quality-score-modal__verdict--${verdictKey}`}>
                                {VERDICT_LABEL[verdictKey]}
                            </div>
                            <SpacingColumn spacing={CRMSpacing.TINY}>
                                <DimensionPill label={DIMENSION_LABELS.clarity} score={props.score.clarity_score} />
                                <DimensionPill label={DIMENSION_LABELS.tone} score={props.score.tone_score} />
                                <DimensionPill label={DIMENSION_LABELS.ownership} score={props.score.ownership_score} />
                            </SpacingColumn>
                            {props.score.suggestions.length > 0 && (
                                <SpacingColumn spacing={CRMSpacing.TINY}>
                                    {props.score.suggestions.map((s, i) => (
                                        <div key={i} className="crm-email-quality-score-modal__suggestion">
                                            <span className="crm-email-quality-score-modal__suggestion-tag">{DIMENSION_LABELS[s.dimension]}: </span>
                                            <span>{s.issue} </span>
                                            <span><em>Try: {s.suggestion}</em></span>
                                        </div>
                                    ))}
                                </SpacingColumn>
                            )}
                            <SpacingRow spacing={CRMSpacing.TINY} justifyContent="end">
                                <CRMButtonTertiary
                                    label={props.rewriteStatus === "loading" ? "Rewriting…" : "Run AI rewrite"}
                                    onClick={props.onRunAiRewrite}
                                    disabled={props.rewriteStatus === "loading"}
                                />
                                <CRMButtonSecondary
                                    label="Let me edit"
                                    onClick={props.onLetMeEdit}
                                />
                                <CRMButtonSecondary
                                    label="Send anyway"
                                    highlight="instructional-brick"
                                    onClick={props.onSendAnyway}
                                />
                            </SpacingRow>
                        </SpacingColumn>
                    </Padding>
                </CRMTidyPopout>
            </Sticky>
        </PopupBase>
    );
};
